import {
  PageStructure,
  SelectedElementContainer,
  useLinearTransform,
  ViewElementContainer
} from '@st/document-viewer-kit'
import { toDragPointerEvent, useUpdateBounds } from '@st/document-viewer-kit'
import { ElementStatus, ToolMessage } from '@st/document-viewer-kit/plugins/annotations'
import { StampAnnotation } from '@st/sdk'
import { ArrowLeftIcon, ArrowRightIcon } from '@st/theme'
import { useMemo, useState } from 'react'
import { DeleteIcon } from '~/components/icons'
import {
  CrossReferencesToolState,
  StampTemplate,
  TextStampTemplateSection
} from '~/st-doc-viewer/annotations'
import { useAnnotationsContext } from '../../annotations-context'
import { AnnotationFloatingToolbar, FloatingToolbarButton } from '../../floating-toolbar'
import { DocumentAnnotationDisplay } from '~/st-doc-viewer/document-annotation-display'
import { recomputeStampLayoutAndStyle } from '../stamps-core/stamp-layout'
import { VisualSelect, VisualSelectSection } from '../stamps-core/visual-select'
import { copyToolState } from './tool-state'
import { REFERENCE_STAMP_TEMPLATE_SECTIONS } from './templates'

function getTemplateById(id: string | undefined): StampTemplate | undefined {
  if (!id) return undefined
  return REFERENCE_STAMP_TEMPLATE_SECTIONS.flatMap((s) => s.templates).find((t) => t.id === id)
}

function crossReferenceAriaLabel(annotation: StampAnnotation) {
  return `${annotation.stampCode} reference on page ${annotation.pageNumber} at x ${Math.round(
    annotation.bounds.x
  )} y ${Math.round(annotation.bounds.y)}`
}

function selEffectiveAnnotation(settings: CrossReferencesToolState['settings']) {
  const template = getTemplateById(settings.templateId)
  if (!template) return undefined
  return recomputeStampLayoutAndStyle(template.annotation)
}

export function CrossRefToolEditor({
  toolState,
  activeAnnotation,
  send
}: {
  toolState: CrossReferencesToolState
  activeAnnotation: StampAnnotation | undefined
  send: (message: ToolMessage<StampAnnotation, CrossReferencesToolState>) => void
}) {
  const [isOpen, setIsOpen] = useState(false)
  const placeholderTemplate = useMemo(
    () => getTemplateById(toolState.settings.templateId),
    [toolState.settings.templateId]
  )

  return (
    <VisualSelect
      className="w-64"
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      placeholder={
        placeholderTemplate ? (
          <div className="h-8">
            <StampOptionPreview template={placeholderTemplate} />
          </div>
        ) : (
          <div className="flex h-8 items-center px-2 text-base font-normal text-gray-700">
            {'Select a reference'}
          </div>
        )
      }
    >
      {REFERENCE_STAMP_TEMPLATE_SECTIONS.map((section: TextStampTemplateSection) => (
        <VisualSelectSection key={section.title} title={section.title} layout={section.layout}>
          {section.templates.map((template: StampTemplate, index: number) => (
            <div
              key={index}
              className="h-10 cursor-pointer hover:bg-gray-100"
              onClick={() => {
                setIsOpen(false)
                send({
                  type: 'updateTool',
                  tool: {
                    name: 'cross_references',
                    settings: { templateId: template.id }
                  }
                })
              }}
            >
              <StampOptionPreview template={template} showDescription={section.layout === 'rows'} />
            </div>
          ))}
        </VisualSelectSection>
      ))}
    </VisualSelect>
  )
}

export function CrossRefToolOverlay({
  page,
  toolState,
  activeAnnotation,
  status,
  send
}: {
  page: PageStructure
  toolState: CrossReferencesToolState
  activeAnnotation: StampAnnotation | undefined
  status: ElementStatus
  send: (message: ToolMessage<StampAnnotation, CrossReferencesToolState>) => void
}) {
  const transform = useLinearTransform()
  const [pointer, setPointer] = useState<{ x: number; y: number } | undefined>(undefined)

  const ghostAnnotation = useMemo(() => {
    return selEffectiveAnnotation(toolState.settings)
  }, [toolState.settings.templateId])

  return (
    <>
      <div
        style={{
          position: 'absolute',
          cursor: 'default',
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          backgroundColor: 'transparent'
        }}
        onPointerMove={(e) => {
          if (activeAnnotation || !ghostAnnotation) {
            setPointer(undefined)
            return
          }
          const pointerEvent = toDragPointerEvent(e, transform, 'drawing-canvas')
          setPointer(pointerEvent.point)
        }}
        onPointerLeave={() => {
          setPointer(undefined)
        }}
        onPointerDown={(e) => {
          e.preventDefault()
          e.stopPropagation()

          const pointerEvent = toDragPointerEvent(e, transform, 'drawing-canvas')

          if (activeAnnotation) {
            send({ type: 'deselectAnnotation' })
            return
          } else if (toolState.settings.templateId) {
            const effective = selEffectiveAnnotation(toolState.settings)
            if (!effective) return
            const { id: _templateId, time: _templateTime, ...template } = effective
            const baseAnnotation = recomputeStampLayoutAndStyle({
              ...template,
              type: 'stamp',
              pageNumber: page.pageNumber,
              bounds: {
                x: pointerEvent.point.x,
                y: pointerEvent.point.y,
                width: 0,
                height: 0
              }
            })
            const annotation = {
              ...baseAnnotation,
              bounds: {
                width: baseAnnotation.bounds.width,
                height: baseAnnotation.bounds.height,
                x: baseAnnotation.bounds.x - baseAnnotation.bounds.width / 2,
                y: baseAnnotation.bounds.y - baseAnnotation.bounds.height / 2
              }
            }

            send({
              type: 'requestAnnotationCreation',
              annotationDraft: annotation
            })
          }
        }}
      />
      {ghostAnnotation && pointer && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            pointerEvents: 'none',
            transform: `translate(${pointer.x}px, ${pointer.y}px)`
          }}
        >
          <div style={{ transform: 'translate(-50%, -50%)' }}>
            <DocumentAnnotationDisplay annotation={ghostAnnotation} />
          </div>
        </div>
      )}
    </>
  )
}

export function CrossRefAnnotationElement({
  annotation,
  page,
  status,
  send
}: {
  annotation: StampAnnotation
  page: PageStructure
  status: ElementStatus
  send: (message: ToolMessage<StampAnnotation, CrossReferencesToolState>) => void
}) {
  switch (status) {
    case 'selected':
    case 'editing':
      return <CrossRefAnnotationSelected annotation={annotation} send={send} />
    case 'none':
      return (
        <ViewElementContainer
          key={annotation.id}
          bounds={annotation.bounds}
          ariaLabel={crossReferenceAriaLabel(annotation)}
          onClick={() => send({ type: 'selectAnnotation', annotationId: annotation.id })}
        >
          <DocumentAnnotationDisplay annotation={annotation} />
        </ViewElementContainer>
      )
  }
}

function CrossRefAnnotationSelected({
  annotation,
  send
}: {
  annotation: StampAnnotation
  send: (message: ToolMessage<StampAnnotation, CrossReferencesToolState>) => void
}) {
  const { crossReferenceNavigation } = useAnnotationsContext()

  const { bounds, onMove } = useUpdateBounds({
    initialBounds: annotation.bounds,
    onCommit: (event) => {
      send({
        type: 'updateAnnotationDraft',
        annotationDraft: { ...annotation, bounds: event.newBounds }
      })
    }
  })

  return (
    <SelectedElementContainer
      bounds={bounds}
      onMove={onMove}
      resizable={false}
      onDoubleClick={() => {
        send({ type: 'beginEditingAnnotation', annotationDraft: annotation })
      }}
      toolbar={
        <AnnotationFloatingToolbar>
          <FloatingToolbarButton
            disabled={!crossReferenceNavigation.previous}
            icon={<ArrowLeftIcon className="size-6" />}
            ariaLabel={`Go to previous ${crossReferenceAriaLabel(annotation)}`}
            onClick={crossReferenceNavigation.previous}
          />
          <FloatingToolbarButton
            disabled={!crossReferenceNavigation.next}
            icon={<ArrowRightIcon className="size-6" />}
            ariaLabel={`Go to next ${crossReferenceAriaLabel(annotation)}`}
            onClick={crossReferenceNavigation.next}
          />
          <FloatingToolbarButton
            icon={<DeleteIcon className="size-6" />}
            ariaLabel={`Delete ${crossReferenceAriaLabel(annotation)}`}
            onClick={() => {
              send({ type: 'deleteAnnotation', annotationId: annotation.id })
            }}
          />
        </AnnotationFloatingToolbar>
      }
    >
      <DocumentAnnotationDisplay annotation={annotation} />
    </SelectedElementContainer>
  )
}

function StampOptionPreview({
  template,
  showDescription = true
}: {
  template: StampTemplate
  showDescription?: boolean
}) {
  return (
    <div className="flex h-full w-full items-center gap-2">
      <div className="flex size-10 items-center justify-center">
        <DocumentAnnotationDisplay annotation={template.annotation} />
      </div>
      {showDescription && (
        <span className="flex items-center text-base font-normal text-gray-700">
          {template.label}
        </span>
      )}
    </div>
  )
}
